import { get } from "lodash";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

/**
 * Shared "is this location still in service?" control for the location lists
 * (regions, areas, districts, assemblies, countries).
 *
 * A retired location keeps every reference pointing at it — member profiles, church
 * maps and stations-served all still resolve — it just stops being offered anywhere a
 * location is picked or filtered on. Every list therefore reads `active` by default and
 * an admin has to ask to see the retired ones.
 */
export const LOCATION_STATUS_OPTIONS = [
  { label: "Active only", value: "active" },
  { label: "Inactive only", value: "inactive" },
  { label: "Active & inactive", value: "all" },
]

export const DEFAULT_LOCATION_STATUS = "active";

export default {
  data() {
    return {
      locationStatusOptions: LOCATION_STATUS_OPTIONS,
      statusFilter: DEFAULT_LOCATION_STATUS,
    }
  },
  computed: {
    locationStatusQuery() {
      return { status: this.statusFilter || DEFAULT_LOCATION_STATUS }
    },
    isDefaultLocationStatus() {
      return (this.statusFilter || DEFAULT_LOCATION_STATUS) === DEFAULT_LOCATION_STATUS
    },
  },
  methods: {
    restoreLocationStatusFromQuery({ status } = {}) {
      if (LOCATION_STATUS_OPTIONS.some((option) => option.value === status)) {
        this.statusFilter = status;
      }
    },
    resetLocationStatus() {
      this.statusFilter = DEFAULT_LOCATION_STATUS;
    },

    /** A row read back from the API. Rows predating the flag carry no `is_active`. */
    isLocationActive(record) {
      return get(record, "is_active") !== false;
    },

    /**
     * Confirms, then flips one location. `serviceMethod` is the name of the
     * `sharedService` call for the level, e.g. `updateGeoDistrictStatus`.
     *
     * Deactivating cascades down the tree, so the confirmation says so rather than
     * letting an admin discover it afterwards.
     */
    async toggleLocationStatus(record, { serviceMethod, label, cascades = true }) {
      const activating = !this.isLocationActive(record);
      const name = get(record, "title") || get(record, "country") || `this ${label.toLowerCase()}`;

      let text = activating
        ? `"${name}" will be offered again wherever locations are picked or filtered.`
        : `"${name}" will stop showing up wherever locations are picked or filtered. Records already pointing at it are not affected.`;

      if (!activating && cascades) {
        text += ` Everything beneath it is deactivated too.`;
      }

      if (activating && cascades) {
        text += ` Anything that was deactivated along with it comes back as well.`;
      }

      const confirmation = await this.$swal({
        title: activating ? `Activate ${label}?` : `Deactivate ${label}?`,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed.",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!confirmation.value) {
        return;
      }

      try {
        this.loading = true;

        const request = await this.useJwt().sharedService[serviceMethod](record._id, activating);

        await this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "CheckCircleIcon",
            variant: "success",
            text: get(request, "data.message") || `${label} updated successfully`,
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
  },
}
